import { useState } from 'react';
import type { ViewBody } from '@ayumia/contracts';
import { evaluateViewCondition } from '@ayumia/component-runtime';
import type { QueryEnvelope } from '../api/client';
import { text } from '../app/i18n';
import { ComponentHost } from '../runtime/ComponentHost';
import { useDeclaredComponent } from '../runtime/useDeclaredComponent';
import { Empty, errorView, Loading } from '../states/States';
import type { ViewResolution } from '@ayumia/contracts';
import { ActionBar } from './ActionBar';
import { detailOf, formOf, type DetailInputs } from './assembly';
import { LinesTab, lineDraft, type LineChoices, type LineDraft } from './LinesTab';
import { ListRepresentation } from './ListRepresentation';
import { fieldLabel, fieldOf, fieldStates, formatValue, type FieldStates, type ObjectSummary } from './format';
import { shortId, useReferenceLabels } from './reference-labels';
import { useLoad } from './useLoad';
import { useAppServices } from './ViewContext';

/**
 * Renderer du composant Core `Detail` (59 §5.4 ; ADR-099) : le même objet que la liste et le formulaire, lu par l'Object
 * API. Un champ retiré par la politique n'arrive pas au navigateur ; un champ masqué s'affiche
 * « Masqué » (ADR-044). Un onglet `component` rend un composant déclaratif avec `$record`.
 */

type Row = Record<string, unknown> & { id?: string; version?: number };
type FieldRef = NonNullable<DetailInputs['header_fields']>[number];

function Fields({ refs, record, object, states }: { refs: readonly FieldRef[]; record: Row; object: ObjectSummary | undefined; states: FieldStates }) {
  const services = useAppServices();
  const shown = refs.filter((ref) => !states.restricted.has(ref.field) && evaluateViewCondition(ref.visible_when, { record, context: services.context }));
  // Une référence s'affiche par le libellé de l'objet cité, comme dans la liste, jamais par son identifiant.
  const references = shown.flatMap((ref) => {
    const field = fieldOf(object, ref.field);
    const value = record[ref.field];
    return field?.type === 'reference' && field.reference_type !== undefined && typeof value === 'string' ? [{ type: field.reference_type, ids: [value] }] : [];
  });
  const labels = useReferenceLabels(services.client, references);
  const display = (fieldId: string): string => {
    const field = fieldOf(object, fieldId);
    const value = record[fieldId];
    if (field?.type === 'reference' && typeof value === 'string') return labels.get(value) ?? shortId(value);
    return formatValue(value, field);
  };
  if (shown.length === 0) return <Empty label="Aucun champ à afficher." />;
  return (
    <dl className="ay-fields">
      {shown.map((ref) => (
        <div key={ref.field} className="ay-fields__item">
          <dt>{fieldLabel(object, ref.field, ref.label)}</dt>
          <dd>{states.masked.has(ref.field) ? <span className="ay-masked">Masqué</span> : display(ref.field)}</dd>
        </div>
      ))}
    </dl>
  );
}

type HistoryEntry = { at: string; command_name: string; label: string; actor_name: string | null; changed_fields?: string[] };

/**
 * Onglet `activity` (S8d) : l'historique de l'enregistrement — ses commandes réussies, lues dans le journal
 * d'audit par `GetObjectHistory`, sous le droit de lire ce type ; de la plus ancienne à la plus récente.
 */
function ActivityTab({ type, record }: { type: string; record: Row }) {
  const services = useAppServices();
  const id = typeof record.id === 'string' ? record.id : '';
  const [loaded, reload] = useLoad<HistoryEntry[]>(`history:${type}/${id}/${String(record.version ?? '')}`, async () => (await services.client.query<HistoryEntry[]>('GetObjectHistory', { object_type: type, id })).data);
  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  if (loaded.value.length === 0) return <Empty label="Aucun historique." />;
  return (
    <ol className="ay-history">
      {loaded.value.map((entry, index) => (
        <li key={`${entry.at}-${index}`} className="ay-history__entry">
          <span className="ay-history__dot" aria-hidden="true" />
          <span className="ay-history__body">
            <strong>{entry.label}</strong>
            <span className="ay-history__meta">
              {new Date(entry.at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
              {entry.actor_name === null ? '' : ` · ${entry.actor_name}`}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function ComponentTab({ artifactId, record }: { artifactId: string; record: Row }) {
  const instance = useDeclaredComponent(artifactId, record);
  if (instance === null) return <Empty label={`Composant indisponible : ${artifactId}`} />;
  return <ComponentHost instance={instance} />;
}

/**
 * Onglet `embedded_view` (ADR-064 §4) : la liste d'une autre vue, restreinte à l'enregistrement
 * affiché par le seul champ de référence de son objet qui vise ce type. Zéro ou plusieurs champs
 * candidats : la configuration est ambiguë, l'onglet le dit au lieu de deviner.
 */
function EmbeddedViewTab({ viewRef, parentType, record }: { viewRef: string; parentType: string; record: Row }) {
  const services = useAppServices();
  const [resolution, reload] = useLoad<ViewResolution>(`embedded:${viewRef}`, async () => (await services.client.query<ViewResolution>('GetView', { view_ref: viewRef })).data);
  if (resolution.status === 'loading') return <Loading />;
  if (resolution.status === 'error') return <>{errorView(resolution.error, reload)}</>;
  const { view, object } = resolution.value;
  const links = (object?.fields ?? []).filter((field) => field.reference_type === parentType && field.filterable === true);
  if (view.body.collection === undefined || links.length !== 1 || typeof record.id !== 'string') return <Empty label={`La vue « ${viewRef} » ne se rattache pas à cet enregistrement.`} />;
  return <ListRepresentation view={view.body} object={object} onOpen={() => undefined} selectedId={null} refreshKey={0} fixedFilter={{ [links[0]!.field_id]: { eq: record.id } }} />;
}

export function DetailRepresentation({ view, object, recordId, onChanged, onEdit }: { view: ViewBody; object: ObjectSummary | undefined; recordId: string; onChanged: () => void; onEdit?: (record: Row) => void }) {
  const services = useAppServices();
  const type = view.target_object_type!;
  const [refresh, setRefresh] = useState(0);
  const [loaded, reload] = useLoad<QueryEnvelope<Row>>(`${type}/${recordId}/${refresh}`, () => services.client.getObject<Row>(type, recordId));
  const detail: DetailInputs = detailOf(view) ?? { tabs: [{ tab_id: 'properties', title: { fr: 'Propriétés' }, kind: 'properties' as const, fields: (object?.fields ?? []).map((field) => ({ field: field.field_id })) }] };
  const tabs = detail.tabs;
  const [selected, setSelected] = useState(tabs[0]?.tab_id ?? '');
  // Décisions ligne par ligne des onglets `lines` (ADR-133 §4) : gardées ici, pour que l'action qui les cite les lise même onglet fermé.
  const [choices, setChoices] = useState<Readonly<Record<string, LineChoices>>>({});

  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  const record = loaded.value.data;
  const states = fieldStates(loaded.value.meta);
  const tab = tabs.find((entry) => entry.tab_id === selected) ?? tabs[0];
  const target = typeof record.id === 'string' ? { type, id: record.id, ...(typeof record.version === 'number' ? { version: record.version } : {}) } : null;
  const drafts = new Map<string, LineDraft>();
  for (const entry of tabs) {
    if (entry.kind !== 'lines') continue;
    const draft = lineDraft(entry, record, choices[entry.tab_id] ?? {}, services.context);
    if (draft !== null) drafts.set(draft.payload_field, draft);
  }

  return (
    <article className="ay-detail">
      {detail.header_fields !== undefined && detail.header_fields.length > 0 && (
        <header className="ay-detail__header">
          <Fields refs={detail.header_fields} record={record} object={object} states={states} />
        </header>
      )}
      <ActionBar
        actions={view.actions ?? []}
        record={record}
        target={target}
        drafts={drafts}
        onDone={() => {
          setChoices({});
          setRefresh((value) => value + 1);
          onChanged();
        }}
      />
      {onEdit !== undefined && formOf(view) !== undefined && (
        <button type="button" className="ay-button ay-button--secondary" onClick={() => onEdit(record)}>
          Modifier
        </button>
      )}
      {/* Explorateur du graphe (ADR-129 §5) : sur un type qui déclare des relations ; présentation, jamais autorisation. */}
      {target !== null && (object?.relations?.length ?? 0) > 0 && (
        <button type="button" className="ay-button ay-button--secondary" onClick={() => services.navigate({ kind: 'graph', object_type: target.type, record_id: target.id })}>
          Explorer les liens
        </button>
      )}
      {tabs.length > 1 && (
        <div role="tablist" className="ay-tabs__list" aria-label="Sections">
          {tabs.map((entry) => (
            <button key={entry.tab_id} type="button" role="tab" id={`tab-${entry.tab_id}`} aria-selected={entry.tab_id === tab?.tab_id} aria-controls={`panel-${entry.tab_id}`} onClick={() => setSelected(entry.tab_id)}>
              {text(entry.title)}
            </button>
          ))}
        </div>
      )}
      {tab !== undefined && (
        <section role="tabpanel" id={`panel-${tab.tab_id}`} aria-labelledby={`tab-${tab.tab_id}`} className="ay-detail__panel">
          {(tab.kind === 'overview' || tab.kind === 'properties') && <Fields refs={tab.fields ?? (object?.fields ?? []).map((field) => ({ field: field.field_id }))} record={record} object={object} states={states} />}
          {tab.kind === 'component' && tab.component !== undefined && <ComponentTab artifactId={tab.component} record={record} />}
          {tab.kind === 'embedded_view' && tab.view_ref !== undefined && <EmbeddedViewTab viewRef={tab.view_ref} parentType={type} record={record} />}
          {tab.kind === 'activity' && <ActivityTab type={type} record={record} />}
          {tab.kind === 'lines' && <LinesTab tab={tab} record={record} choices={choices[tab.tab_id] ?? {}} onChoices={(next) => setChoices((all) => ({ ...all, [tab.tab_id]: next }))} />}
          {tab.kind !== 'overview' && tab.kind !== 'properties' && tab.kind !== 'component' && tab.kind !== 'embedded_view' && tab.kind !== 'activity' && tab.kind !== 'lines' && <Empty label="Cette section arrive avec les phases suivantes." />}
        </section>
      )}
    </article>
  );
}
